import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ShareImageButton from "@/components/ShareImageButton";
import SharePromiseCard from "@/components/SharePromiseCard";
import Header from "@/components/Header";
import QuoteBanner from "@/components/QuoteBanner";
import Footer from "@/components/Footer";
import BigPromisesChart from "@/components/BigPromisesChart";
import PillarCharts from "@/components/PillarCharts";
import { Factory, Tractor, Building2, Landmark, Venus, GraduationCap, Palette, Flag, Plane, Fish, Briefcase, HeartPulse, Scissors, Leaf, PersonStanding, ShieldCheck, House, Users, Church, Trophy, BadgeCheck, BookOpen, Car, Accessibility, Brush, Bike, Baby, Scale, HardHat, TreePalm, Heart, Store, Newspaper, Star, ListChecks } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { fetchSchemes, pick } from "@/lib/insights";

const typeKey = (y) => (y && y.startsWith("Advocacy") ? "Advocacy" : y);
const rs = (s) => (s ? String(s).replace(/Rs\.? ?/g, "₹") : s);
const hasText = (v) => v && v !== "-" && v !== "Not stated";

function PromiseCard({ r, focused }) {
  const { t, lang } = useLang();
  const tk = typeKey(r.type);
  const amount = hasText(r.benefit) ? rs(pick(r, "benefit", lang)) : null;
  const who = hasText(r.beneficiaries) ? rs(pick(r, "beneficiaries", lang)) : null;
  const pillar = (pick(r, "pillar", lang) || "").replace(/^\S+ \d\d – /, "");
  const fromSite = !r.page || r.page === "Website only";
  const title = rs(pick(r, "scheme", lang));
  const pageText = fromSite ? t("from_website") : t("page_n").replace("{g}", r.page);
  const url = `${window.location.origin}/schemes?p=${encodeURIComponent(r.ref)}`;
  return (
    <article id={`promise-${r.ref}`} className={`g-card p-4 sm:px-4 grid gap-2 min-w-0 scroll-mt-6 ${focused ? "outline outline-[3px] outline-[var(--g-accent)]" : ""}`} data-testid={`promise-card-${r.ref}`}>
      <div><span className="text-[12.5px] font-bold px-2.5 py-0.5 rounded-full" style={tk === "Scheme" ? { background: "var(--g-deep-soft)", color: "var(--g-deep)" } : { background: "var(--g-chip)", color: "var(--g-muted)" }}>{t(`ptype_${tk}`)}</span></div>
      <h3 className="m-0 text-[18px] sm:text-[19px] leading-snug font-bold">{title}</h3>
      {amount && <div className="justify-self-start font-bold text-[15px] sm:text-[16px] rounded-lg px-2.5 py-0.5" style={{ color: "var(--g-accent-ink)", background: "var(--g-accent-soft)" }} data-testid={`promise-amount-${r.ref}`}>{amount}</div>}
      <p className="m-0 text-[15px] sm:text-[16px] leading-relaxed">{rs(pick(r, "provisions", lang))}</p>
      <div className="text-[13.5px] flex flex-wrap gap-x-3.5 gap-y-1" style={{ color: "var(--g-muted)" }}>
        {who && <span><b>{t("for_label")}</b> {who}</span>}
        <span>{pillar}</span>
        <span>{pageText}</span>
      </div>
      <ShareImageButton
        className="mt-1"
        testid={`promise-share-${r.ref}`}
        url={url}
        text={t("share_promise_msg").replace("{title}", title)}
        fileName={`neeyosi-tvk-promise-${r.ref}.png`}
        card={<SharePromiseCard typeLabel={t(`ptype_${tk}`)} isBenefit={tk === "Scheme"} title={title} amount={amount} desc={rs(pick(r, "provisions", lang))} who={who} pillar={pillar} pageText={pageText} url={url} t={t} lang={lang} />}
      />
    </article>
  );
}

const AUD_ICON = {
  "Industry, MSMEs & startups": Factory, "Farmers & farm workers": Tractor, "All residents (infrastructure)": Building2,
  "All citizens (governance)": Landmark, "Women & girls": Venus, "Students & parents": GraduationCap,
  "Artists, culture & language": Palette, "All Tamil Nadu (state rights)": Flag, "Tourism & local communities": Plane,
  Fishers: Fish, "Youth & job seekers": Briefcase, "All families (health)": HeartPulse, Weavers: Scissors,
  "Environment – all residents": Leaf, "Senior citizens": PersonStanding, "Police personnel": ShieldCheck,
  "Poor & all households": House, "SC / ST communities": Users, "Religious minorities": Church, "Athletes & sports": Trophy,
  "Government employees": BadgeCheck, Teachers: BookOpen, "Auto/taxi drivers": Car, "Persons with disabilities": Accessibility,
  "Sanitation workers": Brush, "Gig workers": Bike, "Anganwadi & ASHA workers": Baby, "Advocates & legal advisers": Scale,
  "Construction workers": HardHat, "Palm & salt-pan workers": TreePalm, "Transgender persons": Heart,
  "Street vendors": Store, Journalists: Newspaper,
};

function Tile({ active, onClick, icon: Icon, label, sub, top, testid }) {
  const style = top
    ? { background: active ? "var(--g-accent)" : "var(--g-accent-soft)", borderColor: "var(--g-accent)", color: "#2a1800" }
    : { background: active ? "var(--g-deep)" : "var(--g-surface)", borderColor: active ? "var(--g-deep)" : "var(--g-line)", color: active ? "var(--g-bg)" : "var(--g-ink)" };
  return (
    <button onClick={onClick} aria-pressed={active} className="min-w-0 [overflow-wrap:anywhere] text-left border-[1.5px] rounded-xl p-2.5 flex flex-col gap-1 min-h-[84px] hover:-translate-y-0.5 transition-transform" style={style} data-testid={testid}>
      <Icon size={18} style={{ color: active ? "inherit" : top ? "var(--g-accent-ink)" : "var(--g-deep)" }} />
      <b className="text-[13px] leading-tight">{label}</b>
      <span className="text-[11.5px] opacity-75 mt-auto">{sub}</span>
    </button>
  );
}

function SectionList({ sections, group, onPick }) {
  const { t, lang } = useLang();
  const pillars = [...new Set(sections.map((g) => g.pillar))];
  return (
    <details className="mt-4 g-card px-4 py-3" data-testid="sections-fold">
      <summary className="cursor-pointer font-bold text-[15px]" style={{ color: "var(--g-deep)" }} data-testid="sections-fold-toggle">{t("browse_sections")}</summary>
      {pillars.map((p) => (
        <div key={p} className="mt-3">
          <div className="g-display font-bold text-[16px] pb-1 mb-2 border-b" style={{ color: "var(--g-deep)", borderColor: "var(--g-line)" }}>{pick(sections.find((g) => g.pillar === p).sample, "pillar", lang)}</div>
          <div className="flex flex-wrap gap-1.5">
            {sections.filter((g) => g.pillar === p).map((g) => (
              <button key={g.k} onClick={() => onPick(g.k)} aria-pressed={group === g.k} className="text-[13px] rounded-full border px-3 py-1" style={group === g.k ? { background: "var(--g-deep)", color: "var(--g-bg)", borderColor: "var(--g-deep)" } : { borderColor: "var(--g-line)", background: "var(--g-chip)" }} data-testid={`who-${g.k}`}>
                {pick(g.sample, "section", lang)} <span className="opacity-70">· {g.n}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </details>
  );
}

function WhoPicker({ audiences, sections, group, onPick }) {
  const { t, lang } = useLang();
  const count = (n) => (n === 1 ? t("promises_1") : t("promises_n").replace("{n}", n));
  return (
    <>
      <div className={`grid gap-2 ${lang === "ta" ? "grid-cols-2" : "grid-cols-3"} sm:grid-cols-4 lg:grid-cols-6`} data-testid="who-picker">
        <Tile top active={group === "top"} onClick={() => onPick("top")} icon={Star} label={t("top_promises")} sub={t("top_promises_sub")} testid="who-top" />
        <Tile top active={group === "mine"} onClick={() => onPick("mine")} icon={ListChecks} label={t("my_promises")} sub={t("my_promises_sub")} testid="who-mine" />
        {audiences.map((a) => (
          <Tile key={a.k} active={group === a.k} onClick={() => onPick(a.k)} icon={AUD_ICON[a.aud] || Users} label={pick(a.sample, "audience", lang)} sub={count(a.n)} testid={`who-aud-${a.i}`} />
        ))}
      </div>
      <SectionList sections={sections} group={group} onPick={onPick} />
    </>
  );
}

function MyPromises({ items, audiences, onlyBen, picked, setPicked }) {
  const { t, lang } = useLang();
  const toggle = (aud) => setPicked((p) => (p.includes(aud) ? p.filter((x) => x !== aud) : [...p, aud]));
  const groups = audiences.filter((a) => picked.includes(a.aud));
  let total = 0;
  const lists = groups.map((g) => {
    let rows = items.filter((r) => r.audience === g.aud);
    if (onlyBen) rows = rows.filter((r) => typeKey(r.type) === "Scheme");
    rows = [...rows].sort((a, b) => (hasText(b.benefit) ? 1 : 0) - (hasText(a.benefit) ? 1 : 0));
    total += rows.length;
    return { g, rows };
  });
  return (
    <section className="mt-7" data-testid="my-promises">
      <h2 className="g-display font-bold text-[24px] mb-1">{t("my_promises")}</h2>
      <p className="text-[15px] mb-3" style={{ color: "var(--g-muted)" }}>{t("my_pick_title")}</p>
      <div className="flex flex-wrap gap-2" data-testid="my-groups">
        {audiences.map((a) => {
          const on = picked.includes(a.aud);
          const Icon = AUD_ICON[a.aud] || Users;
          return (
            <label key={a.k} className="inline-flex items-center gap-2 rounded-full border-[1.5px] px-3 py-1.5 text-[14px] cursor-pointer select-none transition-colors" style={on ? { background: "var(--g-deep)", color: "var(--g-bg)", borderColor: "var(--g-deep)" } : { background: "var(--g-surface)", borderColor: "var(--g-line)" }} data-testid={`my-group-${a.i}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(a.aud)} className="w-4 h-4" style={{ accentColor: "var(--g-accent)" }} data-testid={`my-group-check-${a.i}`} />
              <Icon size={15} />
              {pick(a.sample, "audience", lang)}
            </label>
          );
        })}
      </div>
      {picked.length > 0 && (
        <button onClick={() => setPicked([])} className="mt-3 text-[14px] font-semibold underline underline-offset-2" style={{ color: "var(--g-deep)" }} data-testid="my-clear">{t("my_clear")}</button>
      )}
      {!picked.length ? (
        <div className="mt-5 p-6 text-center rounded-[14px] border border-dashed" style={{ color: "var(--g-muted)", background: "var(--g-surface)", borderColor: "var(--g-line)" }} data-testid="my-empty">{t("my_none")}</div>
      ) : (
        <>
          <div className="mt-5 g-display font-extrabold text-[22px]" style={{ color: "var(--g-accent-ink)" }} data-testid="my-total">
            {total === 1 ? t("my_total_1") : t("my_total").replace("{n}", total)}
          </div>
          {lists.map(({ g, rows }) => (
            <div key={g.k} className="mt-5" data-testid={`my-list-${g.i}`}>
              <h3 className="g-display font-bold text-[19px] pb-1 mb-2.5 border-b" style={{ color: "var(--g-deep)", borderColor: "var(--g-line)" }}>
                {pick(g.sample, "audience", lang)} · {rows.length}
              </h3>
              <div className="grid gap-3">{rows.map((r) => <PromiseCard key={r.ref} r={r} />)}</div>
            </div>
          ))}
        </>
      )}
    </section>
  );
}

function Highlights({ data, byRef, onOpen }) {
  const { t, lang } = useLang();
  return (
    <>
      <h2 className="g-display font-bold text-[24px] mt-7 mb-2.5">{t("top_glance")}</h2>
      <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]" data-testid="top-highlights">
        {data.map((h) => (
          <button key={h.ref} onClick={() => onOpen(h.ref)} className="g-card text-left px-4 py-3.5 grid gap-1 content-start hover:border-[var(--g-accent)] transition-colors" data-testid={`highlight-${h.ref}`}>
            <div className="g-display font-extrabold text-[28px] leading-none" style={{ color: "var(--g-accent-ink)" }}>{pick(h, "big", lang)}</div>
            <div className="font-bold text-[17px] leading-snug">{pick(h, "what", lang)}</div>
            <div className="text-[14px]" style={{ color: "var(--g-muted)" }}>{rs(pick(byRef[h.ref], "scheme", lang))} · {t("page_short").replace("{g}", byRef[h.ref].page)}</div>
          </button>
        ))}
      </div>
      <p className="text-[15px] mt-3.5" style={{ color: "var(--g-muted)" }}>{t("tap_group")}</p>
    </>
  );
}

function PromiseList({ items, sections, group, q, onlyBen, focusRef }) {
  const { t, lang } = useLang();
  let rows, title;
  if (q) {
    const norm = (s) => s.toLowerCase().replace(/\brs\.? ?(?=\d)/g, "₹");
    const words = norm(q).split(/\s+/).filter(Boolean);
    rows = items.filter((r) => {
      const h = norm([r.scheme, r.provisions, r.beneficiaries, r.benefit, r.section, r.ta?.scheme, r.ta?.provisions, r.ta?.benefit, r.ta?.section].join(" "));
      return words.every((w) => h.includes(w));
    });
    title = t("results_for").replace("{q}", q);
  } else if (group.startsWith("a:")) {
    const aud = group.slice(2);
    rows = items.filter((r) => r.audience === aud);
    title = pick(rows[0], "audience", lang);
  } else {
    const g = sections.find((x) => x.k === group);
    rows = items.filter((r) => r.pillar === g.pillar && r.section === g.section);
    title = pick(g.sample, "section", lang);
  }
  const all = rows.length;
  if (onlyBen) rows = rows.filter((r) => typeKey(r.type) === "Scheme");
  rows = [...rows].sort((a, b) => (hasText(b.benefit) ? 1 : 0) - (hasText(a.benefit) ? 1 : 0));
  const hidden = all - rows.length;
  return (
    <>
      <h2 className="g-display font-bold text-[24px] mt-7 mb-2.5" data-testid="promise-list-title">{title}</h2>
      <p className="text-[15px] mt-3.5 mb-2" style={{ color: "var(--g-muted)" }} data-testid="promise-count">
        {rows.length === 1 ? t("promises_1") : t("promises_n").replace("{n}", rows.length)}{hidden > 0 && t("hidden_n").replace("{n}", hidden)}
      </p>
      {rows.length ? (
        <div className="grid gap-3" data-testid="promise-list">{rows.map((r) => <PromiseCard key={r.ref} r={r} focused={r.ref === focusRef} />)}</div>
      ) : (
        <div className="p-6 text-center rounded-[14px] border border-dashed" style={{ color: "var(--g-muted)", background: "var(--g-surface)", borderColor: "var(--g-line)" }} data-testid="promise-empty">{t("nothing_found")}</div>
      )}
    </>
  );
}

export default function SchemesPage() {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  const [data, setData] = useState(null);
  const [group, setGroup] = useState("top");
  const [q, setQ] = useState("");
  const [onlyBen, setOnlyBen] = useState(false);
  const viewRef = useRef(null);
  const [params] = useSearchParams();
  const [focusRef, setFocusRef] = useState(null);
  const [picked, setPicked] = useState([]);
  useEffect(() => { fetchSchemes().then(setData); }, []);

  const sections = useMemo(() => {
    const out = [];
    for (const r of data?.items || []) {
      let g = out.find((x) => x.pillar === r.pillar && x.section === r.section);
      if (!g) { g = { k: `s${out.length}`, pillar: r.pillar, section: r.section, n: 0, sample: r }; out.push(g); }
      g.n += 1;
    }
    return out;
  }, [data]);
  const audiences = useMemo(() => {
    const m = new Map();
    for (const r of data?.items || []) {
      if (!m.has(r.audience)) m.set(r.audience, { k: `a:${r.audience}`, aud: r.audience, n: 0, sample: r, i: m.size });
      m.get(r.audience).n += 1;
    }
    return [...m.values()].sort((a, b) => b.n - a.n);
  }, [data]);
  const byRef = useMemo(() => Object.fromEntries((data?.items || []).map((r) => [r.ref, r])), [data]);

  function openPromise(ref) {
    const r = byRef[ref];
    if (!r) return;
    setGroup(`a:${r.audience}`); setQ(""); setOnlyBen(false); setFocusRef(ref);
    setTimeout(() => document.getElementById(`promise-${ref}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }

  useEffect(() => {
    const ref = params.get("p");
    if (ref) openPromise(ref);
  }, [params, byRef]); // eslint-disable-line react-hooks/exhaustive-deps

  function onPick(k) {
    setGroup(k); setQ(""); setFocusRef(null);
    setTimeout(() => viewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
  }

  return (
    <div className="App">
      <Header compact title={t("nav_schemes")} />
      <QuoteBanner />
      <main className={`tvk-guide pb-16 ${ta ? "font-tamil" : ""}`} data-testid="schemes-page">
        <div className="max-w-[900px] mx-auto px-4 pt-6">
          <header className="grid gap-1.5 pt-2 pb-4 border-b-[3px]" style={{ borderColor: "var(--g-accent)" }}>
            <div className="text-[13px] uppercase font-semibold" style={{ color: "var(--g-muted)", letterSpacing: ta ? 0 : "0.08em" }}>Tamil Nadu 2026</div>
            <h1 className="g-display font-extrabold text-[30px] sm:text-[42px] leading-[1.05] m-0" data-testid="schemes-title">{t("schemes_title")}</h1>
            <p className="m-0 max-w-[62ch]" style={{ color: "var(--g-muted)" }}>{t("schemes_lede")}</p>
            {ta && <p className="m-0 text-[12px]" style={{ color: "var(--g-muted)" }} data-testid="ai-translation-note">{t("ai_note")}</p>}
          </header>
          {!data ? <div className="mt-6 h-64 rounded-[14px] bg-white/60 animate-pulse" /> : (
            <>
              <div className="flex flex-wrap gap-2.5 items-center mt-4.5 mt-5">
                <input type="search" value={q} onChange={(e) => { setQ(e.target.value); setFocusRef(null); }} placeholder={t("search_promises")} className="flex-[1_1_260px] min-w-0 px-3.5 py-2.5 rounded-[10px] border-[1.5px] outline-none focus:border-[var(--g-accent)]" style={{ borderColor: "var(--g-line)", background: "var(--g-surface)" }} data-testid="promise-search-input" />
                <label className="flex items-center gap-2 text-[15px] cursor-pointer select-none" style={{ color: "var(--g-muted)" }}>
                  <input type="checkbox" checked={onlyBen} onChange={(e) => setOnlyBen(e.target.checked)} className="w-5 h-5" style={{ accentColor: "var(--g-deep)" }} data-testid="promise-only-benefits" />
                  {t("only_benefits")}
                </label>
              </div>
              <h2 className="g-display font-bold text-[24px] mt-7 mb-2.5">{t("who_title")}</h2>
              <p className="-mt-1 mb-3 text-[14px]" style={{ color: "var(--g-muted)" }}>{t("who_sub")}</p>
              <WhoPicker audiences={audiences} sections={sections} group={q.trim() ? null : group} onPick={onPick} />
              <div ref={viewRef} className="scroll-mt-4" />
              {!q.trim() && group === "mine" ? <MyPromises items={data.items} audiences={audiences} onlyBen={onlyBen} picked={picked} setPicked={setPicked} /> : !q.trim() && group === "top" ? <Highlights data={data.highlights} byRef={byRef} onOpen={openPromise} /> : <PromiseList items={data.items} sections={sections} group={group} q={q.trim()} onlyBen={onlyBen} focusRef={focusRef} />}

              <h2 className="g-display font-bold text-[24px] mt-12 mb-3" data-testid="costs-heading">{t("costs_heading")}</h2>
              <div className="space-y-6">
                <BigPromisesChart data={data.big_promises} />
                <PillarCharts pillars={data.pillars} allocationNote={data.allocation_note} />
              </div>
              <p className="mt-6 text-[12px]" style={{ color: "var(--g-muted)" }} data-testid="schemes-disclaimer">{t("schemes_disclaimer")}</p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
